import { useEffect, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Panel, Stack, Text } from '@/ui';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { Accordion, AccordionDetails, AccordionSummary, Fade } from '@/ui/primitives';
import { ExpandIcon } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type ProjectSettingsAccordionProps = {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  defaultExpanded?: boolean;
  preloadContent?: boolean;
  transitionMode?: 'auto' | 'fade' | 'none';
};

export const ProjectSettingsAccordion = ({
  title,
  description,
  children,
  defaultExpanded = false,
  preloadContent = false,
  transitionMode = 'auto',
}: ProjectSettingsAccordionProps): ReactElement => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [hasExpanded, setHasExpanded] = useState(defaultExpanded);

  useEffect(() => {
    if (!expanded) return;
    setHasExpanded(true);
  }, [expanded]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      expanded={expanded}
      slots={transitionMode === 'fade' ? { transition: Fade } : undefined}
      slotProps={{
        transition: {
          mountOnEnter: !preloadContent,
          unmountOnExit: !preloadContent,
          ...(transitionMode === 'none'
            ? { timeout: 0 }
            : transitionMode === 'fade'
              ? { timeout: { enter: 180, exit: 120 } }
              : {}),
          ...(transitionMode === 'none'
            ? {}
            : transitionMode === 'fade'
              ? {}
              : {
                  easing: {
                    enter: 'cubic-bezier(0.2, 0, 0, 1)',
                    exit: 'cubic-bezier(0.4, 0, 1, 1)',
                  },
                }),
        },
      }}
      onChange={(event, nextExpanded) => {
        const target = event.target as HTMLElement | null;
        if (!target?.closest('.MuiAccordionSummary-expandIconWrapper')) {
          return;
        }
        setExpanded(nextExpanded);
      }}
      sx={[
        settingsPanelSurfaceSx,
        {
          border: (theme) => `1px solid ${theme.app.colors.border}`,
          borderRadius: r('card'),
          overflow: 'hidden',
          padding: s('xl'),
          '&::before': { display: 'none' },
        },
      ]}
    >
      <AccordionSummary
        expandIcon={<ExpandIcon fontSize="medium" sx={{ fontSize: 28 }} />}
        sx={{
          padding: s('none'),
          minHeight: 'auto',
          alignItems: 'flex-start',
          cursor: 'default !important',
          userSelect: 'text',
          WebkitUserSelect: 'text',
          '&.MuiButtonBase-root': {
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content': {
            margin: s('none'),
            alignItems: 'flex-start',
            cursor: 'default !important',
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-content .MuiTypography-root': {
            userSelect: 'text',
            WebkitUserSelect: 'text',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            alignSelf: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
          },
        }}
      >
        <Stack gap="xs" sx={{ width: '100%' }}>
          <Text role="sectionTitle">{title}</Text>
          {description ? (
            <Text role="body" color="textSecondary" sx={settingsCopyTextSx}>
              {description}
            </Text>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: s('none'),
          paddingTop: s('lg'),
        }}
      >
        {preloadContent || expanded || hasExpanded ? (
          <Panel container="none" padding="xl">
            {children}
          </Panel>
        ) : null}
      </AccordionDetails>
    </Accordion>
  );
};
